/**
 * Making an over-long value fit the space the artwork leaves for it.
 *
 * A template field may ask for one of three behaviours (`toFitType`):
 *
 *   `scale` - shrink the type until the text fits, down to `minSize`, then cut it
 *   `wrap`  - keep the size, let it wrap inside `toFitWidth`, then cut it
 *   `fixed` - do not fit at all; right-align it at its position
 *
 * WHY THIS IS PURE, AND SYNCHRONOUS.
 *
 * The old renderer did the same job with recursive `setState` inside nested
 * `requestAnimationFrame` and `setTimeout` calls - roughly 200 lines that
 * re-measured the DOM after every single one-pixel step, and that the capture
 * then had to wait 800ms for "in case it has settled". Everything it needed was
 * known before the first render: the text, the font, the size, the ceiling. So
 * the measurement is a function here, the caller passes a measurer, and the
 * answer is available in the same commit as the text.
 *
 * ⚠️ The measurer is injected because the browser's is a canvas and a test's is
 * arithmetic. Nothing in this file touches `document`.
 *
 * ⚠️ **`scale` CUTS WITHOUT AN ELLIPSIS, AND THAT IS FAITHFUL.** The old code
 * built its truncation as `substring(0, mid) + ''` - almost certainly a typo for
 * `'...'`, since the wrap path beside it uses the dots. It is kept because
 * changing it changes what a runner's card says, and only the organizer should
 * decide that a cut name shows a mark. It is reachable only by a value too long
 * for the field even at `minSize`.
 */

/** How much of `toFitWidth` the text is allowed to use. The old renderer's 0.9. */
const TARGET_RATIO = 0.9;
/** How big each line of a two-line ROW is, against the field's size (BS42 artwork). */
export const TWO_LINE_SCALE = 0.5;
/** What a field falls back to when it names no `minSize`. */
const DEFAULT_MIN_SIZE = 10;

/** Measures one line of text at a given size. Width in CSS pixels. */
export type MeasureText = (text: string, fontSizePx: number) => number;

export interface FitRequest {
  text: string;
  fontSize: number;
  /** `scale`, `wrap`, `fixed`, or anything else - which means "do nothing". */
  toFitType?: string | undefined;
  /** The space the artwork leaves. Absent means no fitting is possible. */
  toFitWidth?: number | undefined;
  minSize?: number | undefined;
}

export interface FitResult {
  /** The size to draw at. Unchanged unless `scale` had to shrink it. */
  fontSize: number;
  /** The text to draw. Shorter than the input only when it could not be made to fit. */
  text: string;
  /** `wrap` only: the width the text may wrap inside. */
  wrapWidth?: number;
}

/**
 * Largest size in `[minSize, fontSize]` whose text fits `maxWidth`, or null when
 * even `minSize` is too big.
 *
 * A binary search rather than the old one-pixel-at-a-time loop: the same answer,
 * without re-measuring forty times for a long name.
 */
function largestFittingSize(
  measure: MeasureText,
  text: string,
  maxSize: number,
  minSize: number,
  maxWidth: number,
): number | null {
  if (measure(text, minSize) > maxWidth) return null;
  let low = minSize;
  let high = Math.floor(maxSize);
  let best = minSize;
  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    if (measure(text, middle) <= maxWidth) {
      best = middle;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }
  return best;
}

/** Longest prefix of `text` that fits `maxWidth` at `fontSize`. */
function longestFittingPrefix(measure: MeasureText, text: string, fontSize: number, maxWidth: number): string {
  let low = 0;
  let high = text.length;
  let best = "";
  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    const candidate = text.slice(0, middle);
    if (measure(candidate, fontSize) <= maxWidth) {
      best = candidate;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }
  return best;
}

/** How this field should be drawn, once its value is known. */
export function fitField(field: FitRequest, lineMeasure: MeasureText): FitResult {
  // A ROW like `BIB SNAP` over `1` is two lines (`card-page.ts` `rowText`); it
  // fits when its widest line fits.
  const measure: MeasureText = (text, size) => Math.max(...text.split("\n").map((line) => lineMeasure(line, size)));
  // Two lines share the height one value would take: in the organizer's artwork
  // `VIP` over `2` fills the same box as a single `2`, each line about half.
  const fontSize = field.text.includes("\n") ? Math.round(field.fontSize * TWO_LINE_SCALE) : field.fontSize;
  if (field.toFitType !== "scale" && field.toFitType !== "wrap") return { fontSize, text: field.text };
  // Without a width there is nothing to fit against. The old renderer guessed
  // 90% of the card here; guessing a ceiling that the artwork did not ask for
  // is how a field ends up shrunk for no reason.
  if (field.toFitWidth === undefined || field.toFitWidth <= 0) return { fontSize, text: field.text };

  const maxWidth = field.toFitWidth * TARGET_RATIO;
  const minSize = field.minSize ?? DEFAULT_MIN_SIZE;

  if (field.toFitType === "wrap") {
    // Wrapping is the browser's job - `wrapWidth` is the ceiling it wraps
    // inside. Ours is only the case the browser cannot solve: a single word too
    // long to fit on a line of its own. The dots are the old renderer's, and
    // they appear on this path only.
    const longestWord = field.text.split(/\s+/).reduce((a, b) => (a.length >= b.length ? a : b), "");
    if (longestWord === "" || measure(longestWord, fontSize) <= maxWidth) {
      return { fontSize, text: field.text, wrapWidth: maxWidth };
    }
    const room = maxWidth - measure("...", fontSize);
    const kept = room <= 0 ? "" : longestFittingPrefix(measure, field.text, fontSize, room);
    return { fontSize, text: `${kept}...`, wrapWidth: maxWidth };
  }

  if (measure(field.text, fontSize) <= maxWidth) return { fontSize, text: field.text };

  const shrunk = largestFittingSize(measure, field.text, fontSize, minSize, maxWidth);
  if (shrunk !== null) return { fontSize: shrunk, text: field.text };

  // Too long even at the smallest size the design allows. Cut it there rather
  // than shrink past what the designer said was readable.
  return { fontSize: minSize, text: longestFittingPrefix(measure, field.text, minSize, maxWidth) };
}

/**
 * A measurer over a canvas, which is what the browser uses.
 *
 * One canvas for the whole page, and the font string is rebuilt per call because
 * that is the only way to set it.
 */
export function createCanvasMeasurer(fontFamily: string | undefined, fontWeight: string | undefined): MeasureText {
  const context = document.createElement("canvas").getContext("2d");
  const family = fontFamily ?? "sans-serif";
  const weight = fontWeight ?? "normal";
  return (text, fontSizePx) => {
    if (context === null) return 0;
    context.font = `${weight} ${fontSizePx}px ${family}`;
    return context.measureText(text).width;
  };
}
